import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import EmptyState from '@/components/base/EmptyState';
import useSeo from '@/hooks/useSeo';
import ShareBar from './components/ShareBar';
import { getArticleById, getRelated, formatDate, formatViews } from '@/utils/data';

export default function DetailPage() {
  const { id = '' } = useParams();
  const article = getArticleById(id);
  const related = article ? getRelated(article, 4) : [];

  useSeo({
    title: article ? `${article.title} - 色哟哟娱乐资讯` : '资讯不存在 - 色哟哟',
    description: article ? article.summary : '该资讯不存在或已删除',
    keywords: article ? `色哟哟,${article.categoryName},${article.tags.join(',')}` : '色哟哟',
  });

  if (!article) {
    return (
      <div className="w-full px-4 py-16 md:px-8">
        <EmptyState
          icon="ri-file-warning-line"
          title="该资讯不存在或已删除"
          desc="你访问的资讯可能已被移除，或链接有误。返回首页看看其他精彩内容吧。"
          actionLabel="返回首页"
          linkTo="/"
        />
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="w-full px-4 py-6 md:px-8 md:py-8">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: article.categoryName, to: `/category/${article.category}` }, { label: '正文' }]} />
      </div>

      <article className="w-full px-4 pb-12 md:px-8">
        <header className="mx-auto max-w-3xl">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Link
              to={`/category/${article.category}`}
              className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200 cursor-pointer"
            >
              {article.categoryName}
            </Link>
            {article.hot && (
              <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-700">
                <i className="ri-fire-fill"></i>
                热门
              </span>
            )}
          </div>
          <h1 className="text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">{article.title}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-500">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {formatDate(article.date)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-eye-line"></i>
              {formatViews(article.views)} 阅读
            </span>
          </div>
        </header>

        <div className="mx-auto mt-6 max-w-4xl overflow-hidden rounded-lg bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 色哟哟娱乐资讯`}
            className="h-56 w-full object-cover object-top md:h-[420px]"
          />
        </div>

        <div className="mx-auto mt-8 max-w-3xl">
          {article.content.map((block, i) => {
            if (block.type === 'heading') {
              return (
                <h2 key={i} className="mb-3 mt-8 flex items-center gap-2 text-xl font-bold text-foreground-900 md:text-2xl">
                  <span className="h-4 w-1 rounded-full bg-primary-500"></span>
                  {block.text}
                </h2>
              );
            }
            if (block.type === 'image') {
              return (
                <figure key={i} className="my-6">
                  <div className="overflow-hidden rounded-lg bg-background-200">
                    <img
                      src={block.src}
                      alt={block.caption || article.title}
                      title={`${article.title} - 色哟哟娱乐资讯`}
                      className="h-56 w-full object-cover object-top md:h-80"
                    />
                  </div>
                  {block.caption && (
                    <figcaption className="mt-2 text-center text-xs text-foreground-400">{block.caption}</figcaption>
                  )}
                </figure>
              );
            }
            if (block.type === 'quote') {
              return (
                <blockquote
                  key={i}
                  className="my-6 rounded-lg border-l-4 border-primary-500 bg-background-100 p-4 text-sm italic text-foreground-700 md:text-base"
                >
                  {block.text}
                </blockquote>
              );
            }
            return (
              <p key={i} className="mb-5 text-base leading-relaxed text-foreground-700">
                {block.text}
              </p>
            );
          })}

          <div className="mt-8 flex flex-wrap items-center gap-2">
            {article.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
                #{tag}
              </span>
            ))}
          </div>

          <div className="mt-8">
            <ShareBar title={article.title} />
          </div>

          <div className="mt-8 border-t border-background-200 pt-6">
            <Link
              to={`/category/${article.category}`}
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回「{article.categoryName}」分类
            </Link>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="w-full bg-background-100 px-4 py-10 md:px-8 md:py-14">
          <div className="mb-6 flex items-center gap-3">
            <span className="flex h-1.5 w-1.5 rounded-full bg-primary-500"></span>
            <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
              <i className="ri-book-open-line text-primary-500"></i>
              相关推荐
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-4">
            {related.map((a) => (
              <ArticleCard key={a.id} article={a} showSummary={false} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}